import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance } from '../config';
import { medicineDraftModels } from './medicineDraftModels.mjs';
import { requestErrorDetails } from './requestError.mjs';

const allowedModes = ['auto', 'free', 'paid'];
const saved = (key, fallback) => { try { return localStorage.getItem(key) || fallback; } catch { return fallback; } };

export default function useSeoModels({storagePrefix='seo',defaultBillingMode='auto'}={}) {
  const [preferred, setPreferred] = useState(() => saved(`${storagePrefix}.preferredModel`, ''));
  const [billingMode, setMode] = useState(() => { const value = saved(`${storagePrefix}.billingMode`, defaultBillingMode); return allowedModes.includes(value) ? value : defaultBillingMode; });
  const [state, setState] = useState({ catalogue: null, error: '', loading: true, mode: '' });
  const sequence = useRef(0);
  const load = useCallback(async mode => {
    const request = ++sequence.current;
    setState({ catalogue: null, error: '', loading: true, mode });
    try {
      const response = await axiosInstance.get('/api/seo/models', { params: { mode }, timeout: 15000 });
      if (sequence.current === request) setState({ catalogue: response.data, error: '', loading: false, mode });
    } catch (failure) {
      if (sequence.current === request) setState({ catalogue: null, error: requestErrorDetails(failure, { readOnly: true, fallback: 'SEO models could not be loaded.' }).message, loading: false, mode });
    }
  }, []);
  const invalidate = useCallback(() => { sequence.current++; }, []);
  useEffect(() => { load(billingMode); return invalidate; }, [billingMode, invalidate, load]);
  const effective = medicineDraftModels(state.mode === billingMode ? state.catalogue : null, preferred);
  return {
    ...effective, billingMode, loading: state.loading || state.mode !== billingMode,
    error: state.mode === billingMode ? state.error : '',
    setBillingMode: value => {
      if (!allowedModes.includes(value)) return;
      setMode(value); try { localStorage.setItem(`${storagePrefix}.billingMode`, value); } catch {}
    },
    setSelection: value => {
      if (!effective.catalogue?.models.some(model => model.id === value && model.enabled)) return;
      setPreferred(value); try { localStorage.setItem(`${storagePrefix}.preferredModel`, value); } catch {}
    },
    refresh: () => load(billingMode),
  };
}
